/**
 * WEBSOCKET / SIGNALR FAN SERVICE: ESQUIVADOR (SURFER)
 * Wrapper de comunicación en tiempo real para el Mando Móvil del Aficionado (Jugador 1 o Jugador 2).
 * Gestiona la conexión persistente con SignalR Hub (`/hub/entertainment`) para recibir la cuenta regresiva sincronizada,
 * eventos de partida (inicio, pausa, reanudación, fin, reinicio), avisos de impacto (para vibración háptica),
 * actualización de puntuaciones y el evento prioritario de desalojo 'kicked_out' (Patrón Tacos).
 * 
 * @module utilities/WS/SurferFanWS
 */

import SignalRService from './SignalRService';

/**
 * Referencia al handler de escucha registrado en SignalRService para mensajes generales.
 * @type {Function|null}
 */
let registeredMessageHandler = null;

/**
 * Referencia al handler de reconexión exitosa registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectedHandler = null;

/**
 * Referencia al handler de reconexión en progreso registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectingHandler = null;

/**
 * Referencia al handler de cierre definitivo de conexión.
 * @type {Function|null}
 */
let registeredCloseHandler = null;

/**
 * Normaliza la clave del rol de conexión para el Hub de SignalR según el bando del jugador.
 * 
 * @param {string} [playerSide=''] - Bando o slot del jugador ('player1' | 'player2' | 'J1' | 'J2' | '1' | '2' | 'fan').
 * @returns {string} Rol de conexión formal ('surfer:player1' | 'surfer:player2' | 'surfer:fan').
 */
const resolvePlayerRole = (playerSide = '') => {
    const normalized = String(playerSide || '').toLowerCase().trim();
    if (normalized === 'player1' || normalized === 'j1' || normalized === 'p1' || normalized === '1' || normalized === 'left') {
        return 'surfer:player1';
    }
    if (normalized === 'player2' || normalized === 'j2' || normalized === 'p2' || normalized === '2' || normalized === 'right') {
        return 'surfer:player2';
    }
    return 'surfer:fan';
};

/**
 * Limpia las claves de sessionStorage asociadas a la sesión del aficionado en la sala.
 * Se ejecuta obligatoriamente al recibir el evento 'kicked_out' o al resetear la sesión.
 * 
 * @param {string} [targetRoomId=''] - Identificador GUID o nombre de la sala.
 */
const clearSurferFanSession = (targetRoomId = '') => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return;

        const cleanRoomId = String(targetRoomId || '').toLowerCase().trim();

        // Limpieza de claves directas conocidas
        if (cleanRoomId) {
            sessionStorage.removeItem(`surfer_fan_session_${cleanRoomId}`);
            sessionStorage.removeItem(`surfer_player_${cleanRoomId}`);
            sessionStorage.removeItem(`surfer_session_${cleanRoomId}`);
            sessionStorage.removeItem(`surfer_role_${cleanRoomId}`);
            sessionStorage.removeItem(`surfer_fan_data_${cleanRoomId}`);
        }
        sessionStorage.removeItem('surfer_fan_session');
        sessionStorage.removeItem('surfer_player_choice');
        sessionStorage.removeItem('surfer_fan_client_id');
        sessionStorage.removeItem('surfer_active_room');

        // Barrido defensivo de claves que contengan 'surfer' y el roomId
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key) {
                const lowerKey = key.toLowerCase();
                if (lowerKey.includes('surfer') && (cleanRoomId === '' || lowerKey.includes(cleanRoomId))) {
                    keysToRemove.push(key);
                }
            }
        }
        keysToRemove.forEach(k => sessionStorage.removeItem(k));
    } catch (storageErr) {
        console.warn('[SurferFanWS] Error al limpiar sessionStorage de la sala:', storageErr);
    }
};

/**
 * Conecta el dispositivo móvil del aficionado al Hub de SignalR con el rol 'surfer:player1', 'surfer:player2' o 'surfer:fan'.
 * 
 * Gestiona de forma prioritaria el evento `kicked_out` (`reason: "CHARACTER_REASSIGNED"` - Patrón Tacos):
 * Cuando otro aficionado toma el mismo personaje o rol, se limpia el sessionStorage de la sala,
 * se invoca `onKickedOut(messageEnvelope)` y se alerta al componente de interfaz móvil.
 * 
 * Eventos clave recibidos y procesados:
 * - `surfer_countdown_tick`: Conteo regresivo sincronizado hacia el inicio.
 * - `surfer_match_started`: Inicio de la carrera/partida activa.
 * - `surfer_match_paused` / `surfer_match_resumed`: Notificaciones de pausa y reanudación de la partida.
 * - `surfer_player_hit`: Impacto de obstáculo o daño (utilizado para activar vibración háptica en el mando).
 * - `surfer_score_updated`: Actualización de puntuación en vivo, distancia y monedas.
 * - `surfer_match_finished`: Fin de la partida y resumen final de resultados.
 * - `surfer_match_reset`: Partida reiniciada al estado de espera inicial.
 * - `kicked_out`: Notificación de desalojo forzoso por reasignación de personaje/rol.
 * 
 * @param {Function} [onIncomingMessage=() => {}] - Callback para procesar mensajes y eventos emitidos por el Hub.
 * @param {Function} [onStatusChange=() => {}] - Callback para notificar cambios de estado ('connecting', 'connected', 'reconnecting', 'error', 'closed').
 * @param {string} [roomId=''] - Identificador GUID de la sala para filtrar el tráfico de eventos.
 * @param {string} [playerSide=''] - Bando o slot del jugador ('player1' | 'player2' | 'J1' | 'J2').
 * @param {Function} [onKickedOut=() => {}] - Callback específico ejecutado al recibir el evento 'kicked_out'.
 */
export const ConnectWebSocketForSurferFan = (
    onIncomingMessage = () => {},
    onStatusChange = () => {},
    roomId = '',
    playerSide = '',
    onKickedOut = () => {}
) => {
    onStatusChange('connecting');

    // Desuscribir handlers previos si existieran para evitar fugas de memoria o ejecuciones duplicadas
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
    }

    // Handler de mensajes entrantes con filtrado estricto por sala y detección de kicked_out
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        // 1. FILTRADO ESTRICTO POR ROOM ID (AISLAMIENTO MULTI-TENANT)
        const envelopeRoomId = messageEnvelope.roomId
            || messageEnvelope.RoomId
            || messageEnvelope.payload?.roomId
            || messageEnvelope.payload?.RoomId
            || messageEnvelope.data?.roomId
            || messageEnvelope.data?.RoomId;

        if (roomId && envelopeRoomId && String(envelopeRoomId).toLowerCase() !== String(roomId).toLowerCase()) {
            return;
        }

        const eventType = String(messageEnvelope.type || messageEnvelope.Type || '').toLowerCase();

        // 2. MANEJO PRIORITARIO DEL EVENTO KICKED_OUT (PATRÓN TACOS / CHARACTER_REASSIGNED)
        if (eventType === 'kicked_out') {
            console.warn('[SurferFanWS] Evento kicked_out recibido para esta sala:', messageEnvelope);

            // Limpieza inmediata y obligatoria de la sesión en sessionStorage para esta sala
            clearSurferFanSession(roomId);

            // Invocar el callback específico de desalojo
            if (typeof onKickedOut === 'function') {
                try {
                    onKickedOut(messageEnvelope);
                } catch (kickCbErr) {
                    console.error('[SurferFanWS] Error en onKickedOut callback:', kickCbErr);
                }
            }

            // También despachar al handler general si está registrado
            if (typeof onIncomingMessage === 'function') {
                try {
                    onIncomingMessage(messageEnvelope);
                } catch (dispatchError) {
                    console.error('[SurferFanWS] Error en onIncomingMessage para kicked_out:', dispatchError);
                }
            }
            return;
        }

        // 3. DESPACHO AL HANDLER GENERAL PARA EVENTOS DE LA DINÁMICA
        if (typeof onIncomingMessage === 'function') {
            try {
                onIncomingMessage(messageEnvelope);
            } catch (dispatchError) {
                console.error('[SurferFanWS] Error en onIncomingMessage callback:', dispatchError);
            }
        }
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = () => {
        onStatusChange('reconnecting');
    };

    registeredCloseHandler = () => {
        onStatusChange('closed');
    };

    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);

    const connectionRole = resolvePlayerRole(playerSide);

    SignalRService.connect({
        tipo: connectionRole,
        roomId: roomId || '',
        playerSide: playerSide || ''
    })
        .then(() => {
            onStatusChange('connected');
        })
        .catch((connectionError) => {
            console.error('[SurferFanWS] Error al conectar el aficionado con SignalR Hub:', connectionError);
            onStatusChange('error');
        });
};

/**
 * Desconecta limpiamente la sesión SignalR del aficionado móvil y remueve los escuchas registrados.
 */
export const DisconnectWebSocketForSurferFan = () => {
    try {
        if (registeredMessageHandler) {
            SignalRService.off('message', registeredMessageHandler);
            registeredMessageHandler = null;
        }
        if (registeredReconnectedHandler) {
            SignalRService.off('reconnected', registeredReconnectedHandler);
            registeredReconnectedHandler = null;
        }
        if (registeredReconnectingHandler) {
            SignalRService.off('reconnecting', registeredReconnectingHandler);
            registeredReconnectingHandler = null;
        }
        if (registeredCloseHandler) {
            SignalRService.off('close', registeredCloseHandler);
            registeredCloseHandler = null;
        }
        SignalRService.disconnect();
    } catch (disconnectionError) {
        console.error('[SurferFanWS] Error al desconectar la sesión de SignalR del aficionado:', disconnectionError);
    }
};

export default {
    ConnectWebSocketForSurferFan,
    DisconnectWebSocketForSurferFan
};
